import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { FaCamera, FaSpinner, FaUserCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { UPLOAD_URL } from "../utils/constants";
import { useLanguage } from "../context/LanguageContext";

type ProfileImageUploadProps = {
  setImage: (file: File) => void;
  existingImage?: string | null;
  onProcessingChange?: (processing: boolean) => void;
  disabled?: boolean;
};

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_IMAGE_WIDTH = 1024;
const JPEG_QUALITY = 0.75;

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  setImage,
  existingImage,
  onProcessingChange,
  disabled = false,
}) => {
  const { t } = useLanguage();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [imageError, setImageError] = useState(false);

  const previewUrlRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const processingRef = useRef(false);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const loadImage = (source: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error("Unable to load the selected image"));

      image.src = source;
    });
  };

  const canvasToBlob = (
    canvas: HTMLCanvasElement,
    type: string,
    quality: number,
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Unable to compress the image"));
          }
        },
        type,
        quality,
      );
    });
  };

  const compressImage = async (file: File): Promise<File> => {
    const sourceUrl = URL.createObjectURL(file);

    try {
      const image = await loadImage(sourceUrl);

      const scale = Math.min(1, MAX_IMAGE_WIDTH / image.naturalWidth);

      const width = Math.max(1, Math.round(image.naturalWidth * scale));

      const height = Math.max(1, Math.round(image.naturalHeight * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas is not supported");
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      context.drawImage(image, 0, 0, width, height);

      const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);

      const originalName = file.name.replace(/\.[^/.]+$/, "") || "profile";

      return new File([blob], `${originalName}-${Date.now()}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  };

  const handleImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || processingRef.current || disabled) {
      event.target.value = "";
      return;
    }

    event.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("Please select an image smaller than 20 MB");
      return;
    }

    processingRef.current = true;
    setIsCompressing(true);
    setIsPreviewLoading(true);
    setImageError(false);
    onProcessingChange?.(true);

    revokePreviewUrl();

    // Show the original image immediately while compression happens.
    const previewUrl = URL.createObjectURL(file);

    previewUrlRef.current = previewUrl;
    setImagePreview(previewUrl);

    try {
      const compressedFile = await compressImage(file);

      setImage(compressedFile);
    } catch (error) {
      console.error("Image compression failed:", error);

      // Continue with the original file if compression fails.
      setImage(file);

      toast.warning(
        "Image optimization failed. The original image will be used.",
      );
    } finally {
      processingRef.current = false;
      setIsCompressing(false);
      onProcessingChange?.(false);
    }
  };

  const handlePreviewLoaded = () => {
    setIsPreviewLoading(false);
    setImageError(false);
  };

  const handlePreviewError = () => {
    setIsPreviewLoading(false);
    setImageError(true);
  };

  useEffect(() => {
    return () => {
      revokePreviewUrl();
    };
  }, []);

  const existingImageUrl = existingImage
    ? `${UPLOAD_URL}/users/profile_pic/${existingImage}`
    : null;

  const displayedImage = imagePreview || existingImageUrl;

  return (
    <div className="flex flex-col items-center">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        id="profileImage"
        onChange={handleImageSelect}
        disabled={isCompressing || disabled}
        className="hidden"
      />

      <label
        htmlFor={isCompressing || disabled ? undefined : "profileImage"}
        className={`relative group ${
          isCompressing || disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <div className="relative h-32 w-32 overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-md">
          {displayedImage && !imageError ? (
            <img
              src={displayedImage}
              alt="Profile Preview"
              className={`h-full w-full object-cover transition-opacity duration-200 ${
                isPreviewLoading ? "opacity-0" : "opacity-100"
              }`}
              onLoad={handlePreviewLoaded}
              onError={handlePreviewError}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FaUserCircle size={80} className="text-gray-300" />
            </div>
          )}

          {isPreviewLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
              <FaSpinner size={24} className="animate-spin text-red-500" />

              <span className="mt-2 text-xs text-gray-500">Loading...</span>
            </div>
          )}

          {isCompressing && !isPreviewLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 text-white">
              <FaSpinner size={22} className="animate-spin" />

              <span className="mt-2 text-xs">Optimizing...</span>
            </div>
          )}
        </div>

        <div
          className={`absolute bottom-0 right-0 rounded-full p-2 text-white shadow-lg transition ${
            isCompressing || disabled
              ? "bg-gray-400"
              : "bg-red-500 group-hover:bg-red-600"
          }`}
        >
          {isCompressing ? (
            <FaSpinner size={16} className="animate-spin" />
          ) : (
            <FaCamera size={16} />
          )}
        </div>
      </label>

      <p className="mt-3 text-sm text-gray-500">
        {isCompressing
          ? "Optimizing selected photo..."
          : disabled
            ? "Image actions are temporarily locked."
          : t("Click the icon to upload your photo")}
      </p>

      {imageError && (
        <p className="mt-2 text-sm text-red-500">
          Unable to display this image. Please select another photo.
        </p>
      )}
    </div>
  );
};

export default ProfileImageUpload;
